import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApi } from "@/src/lib/hooks/api/use-api";
import Icon, { type IconName } from "../custom/Icon";
import { Button } from "../ui/button";

interface INavigationItem {
	icon: IconName;
	path: string;
	label: string;
}

export default function Sidebar() {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { auth, orders } = useApi();

	const { data: session } = auth.useSession();
	const { data: ordersData } = orders.getByUserId({
		userId: session?.user?.id || "",
	});

	// Filter active orders (received, preparing, out-for-delivery)
	const activeOrders =
		ordersData?.orders?.filter((order) =>
			["received", "preparing", "out-for-delivery"].includes(order.status),
		) || [];

	const hasActiveOrders = activeOrders.length > 0;

	const navigationItems: INavigationItem[] = [
		{ icon: "LayoutGrid", path: "/", label: t("nav.dashboard") },
		{ icon: "HandPlatter", path: "/orders", label: t("nav.orders") },
		{ icon: "User", path: "/profile", label: t("nav.profile") },
		{ icon: "Settings", path: "/settings", label: t("nav.settings") },
		{ icon: "CircleHelp", path: "/support", label: t("nav.support") },
	];

	return (
		<div className="fixed top-(--navbar-height) h-[calc(100dvh-var(--navbar-height))] w-(--sidebar-width) left-0 z-40 bg-background hidden sm:flex">
			<div className="flex flex-col w-full gap-6 px-4 mt-6">
				{navigationItems.map((item) => (
					<Button
						key={item.path}
						variant={pathname === item.path ? "primary" : "ghost"}
						className="rounded-full relative"
						asChild
					>
						<Link to={item.path}>
							<Icon name={item.icon} className="size-5" />
							{item.path === "/orders" && hasActiveOrders && (
								<span className="absolute -top-2 -right-2 bg-chart-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
									{activeOrders.length > 99 ? "99+" : activeOrders.length}
								</span>
							)}
						</Link>
					</Button>
				))}
			</div>
		</div>
	);
}
